import { useEffect, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../partage/api/client';
import { cles } from '../../partage/relectures';
import type { JourneeDuJour as Donnees, TacheDuJour } from '../../partage/api/types';
import { FeuilleDuBas } from '../../partage/FeuilleDuBas';
import { useCocher, usePlusTardAujourdhui, useRepondreAuBandeau } from '../journeeDuJour';
import {
  ANNULER,
  C_EST_FAIT,
  CHANGER_MA_JOURNEE,
  cEstFaitPour,
  faitesEtPrevues,
  IMPOSE,
  libereDesChoses,
  MA_JOURNEE,
  MOTS_DE_L_HEURE,
  PAS_AUJOURDHUI,
  PAS_AUJOURDHUI_NOTE,
  passeEnFinDeJournee,
  PLUS_TARD,
  PLUS_TARD_AUJOURDHUI,
  PLUS_TARD_AUJOURDHUI_NOTE,
  plusTardPour,
  remettreAFaire,
  remiseAFaire,
  sortDeTaJournee,
  TOUT_EST_FAIT,
} from '../../partage/vocabulaire';
import { dureeLisible, heureLisible } from './datesDeLAgenda';
import { ceQueCaLibere } from './JourneeDuJour';
import { compteDuJour, DUREE_D_ANNULER_MS, heureAvancee, lignesDuMoment, tacheDeLHeure, toutEstFait } from './journeeDuMoment';
import { enMots } from './refusDuJour';
import './JourneeDuJour.css';
import './ListeAuChoix.css';
import { teinteDuProjet } from '../../apparence';
import { FichePosee } from '../../fiche';
import '../../partage/PiecesDuCarnet.css';
import './JourneeAuPouce.css';

const teinte = (t: TacheDuJour): CSSProperties => ({ ['--teinte' as string]: teinteDuProjet(t.dossier?.couleur) });

/** La première tâche à faire de la journée, dans son ordre ; sinon la première. */
function premiereAFaire(taches: readonly TacheDuJour[]): string | null {
  return (taches.find((t) => t.etat !== 'fait') ?? taches[0])?.bulleId ?? null;
}

/**
 * La journée en cours, dans l'onglet Journée du téléphone (chantier `telephone-journee-agenda`,
 * ticket 04, décisions d'Igor du 2026-10-05, 7, 8, 11 et 20 ; maquette `design/telephone.html`,
 * piste « Journée », « La journée en cours »).
 *
 * En haut, la barre « 1 sur 3 faites · 1 h 20 prévues ». Puis la journée à ses heures : ce qui
 * est fait s'efface (pâli, barré, à sa place) ; la tâche de l'heure en grand, avec « C'est fait »
 * et « Plus tard » ; le reste sur la page du cahier (chantier telephone-ecrans, 01, maquette
 * `journee()`) : l'heure à la main dans la marge, la case, le titre, le projet et la durée, › ;
 * le rendez-vous du jour encadré, sans geste. Toucher une tâche ouvre sa fiche (la feuille de sa
 * mère, la fiche dessus) ; la case la coche sans l'ouvrir. Après une coche, « Annuler » quelques
 * secondes. « Plus tard » ouvre deux choix : « Plus tard aujourd'hui » (le serveur la passe en
 * fin de journée et les heures suivent) ou « Pas aujourd'hui » (elle sort de la journée,
 * libre dans son projet). Toute faite : « Tout est fait ». « Changer ma journée » la rouvre.
 *
 * L'ordinateur garde `JourneeDuJour`.
 */
export function JourneeAuPouce({
  donnees,
  luA,
  onOuvrirLaFiche,
  onChanger,
  onAnnonce,
  couche = false,
}: {
  donnees: Donnees & { journee: NonNullable<Donnees['journee']> };
  /** Le téléphone couché : la bande des pastilles et la fiche posée. */
  couche?: boolean;
  /** Quand la journée a été lue (en ms) : l'heure du serveur avance d'autant. */
  luA: number;
  onOuvrirLaFiche: (t: { bulleId: string; parentId: string | null }) => void;
  onChanger: () => void;
  onAnnonce: (texte: string) => void;
}) {
  const { journee } = donnees;
  const jour = journee.jour;
  const cocher = useCocher();
  const plusTardAujourdhui = usePlusTardAujourdhui();
  const liberer = useRepondreAuBandeau();
  const [annuler, setAnnuler] = useState<{ tache: TacheDuJour; texte: string } | null>(null);
  const [plusTard, setPlusTard] = useState<TacheDuJour | null>(null);
  // Couché, la pastille choisie : la première tâche à faire à l'ouverture.
  const [choisie, setChoisie] = useState<string | null>(() => premiereAFaire(journee.taches));

  // Les rendez-vous du jour.
  const preparation = useQuery({ queryKey: cles.preparation(jour, 'en-cours'), queryFn: () => api.preparation(jour) });
  const rendezVous = preparation.data?.impose.rendezVous ?? [];

  // « Annuler » ne reste que quelques secondes.
  useEffect(() => {
    if (!annuler) return;
    const minuteur = window.setTimeout(() => setAnnuler(null), DUREE_D_ANNULER_MS);
    return () => window.clearTimeout(minuteur);
  }, [annuler]);

  // La tâche de l'heure suit l'horloge, minute par minute, sans relire la journée.
  const [instant, setInstant] = useState(() => Date.now());
  useEffect(() => {
    const tic = window.setInterval(() => setInstant(Date.now()), 60_000);
    return () => window.clearInterval(tic);
  }, []);

  const occupe = cocher.isPending || plusTardAujourdhui.isPending || liberer.isPending;
  const compte = compteDuJour(journee.taches);
  const fini = toutEstFait(journee.taches);
  const deLHeure = tacheDeLHeure(journee.taches, heureAvancee(donnees.heure, (instant - luA) / 60_000));
  const lignes = lignesDuMoment(journee.taches, rendezVous);

  function basculer(t: TacheDuJour, fait: boolean) {
    setAnnuler(null);
    cocher.mutate(
      { id: t.bulleId, fait },
      {
        onSuccess: () => (fait ? setAnnuler({ tache: t, texte: ceQueCaLibere(t) }) : onAnnonce(remiseAFaire(t.titre))),
        onError: (e) => onAnnonce(enMots(e)),
      }
    );
  }

  function plusTardAujourdhuiPour(t: TacheDuJour) {
    setPlusTard(null);
    plusTardAujourdhui.mutate(t.bulleId, {
      onSuccess: () => onAnnonce(passeEnFinDeJournee(t.titre)),
      onError: (e) => onAnnonce(enMots(e)),
    });
  }

  function pasAujourdhui(t: TacheDuJour) {
    setPlusTard(null);
    liberer.mutate(
      { jour, reponse: { geste: 'liberer', taches: [t.bulleId] } },
      { onSuccess: () => onAnnonce(sortDeTaJournee(t.titre)), onError: (e) => onAnnonce(enMots(e)) }
    );
  }

  /*
   * Couché (chantier telephone-couche-suite, 03, maquette `cJournee()`, `.bande`, `.pas`,
   * `.fiche-l`) : la journée en pastilles dans une bande, dans l'ordre des heures (les mêmes
   * lignes que droite), et dessous la fiche du téléphone de la tâche choisie, posée dans l'écran
   * (`FichePosee`). Toucher une pastille change de fiche, sans voile.
   */
  if (couche) {
    const ids = lignes.map((l) => (l.sorte === 'tache' ? l.tache.bulleId : l.rendezVous.id));
    const montree = choisie !== null && ids.includes(choisie) ? choisie : premiereAFaire(journee.taches);
    return (
      <section className="journee encours encours--couche" aria-label={MA_JOURNEE} data-tuto="journee">
        <ol className="bande-journee">
          {lignes.map((l) => {
            const rdv = l.sorte === 'rendez_vous';
            const id = rdv ? l.rendezVous.id : l.tache.bulleId;
            const fait = !rdv && l.tache.etat === 'fait';
            const classe = ['bande-journee-pastille', id === montree && 'est-choisie', fait && 'est-faite', rdv && 'est-rdv']
              .filter(Boolean)
              .join(' ');
            return (
              <li key={rdv ? `rdv-${id}` : id} data-tache={rdv ? undefined : id} data-rdv={rdv ? id : undefined}>
                <button type="button" className={classe} aria-pressed={id === montree} onClick={() => setChoisie(id)}>
                  <small>{heureLisible(rdv ? l.rendezVous.heure : l.tache.heure)}</small>
                  {rdv ? l.rendezVous.titre : l.tache.titre}
                </button>
              </li>
            );
          })}
        </ol>
        {montree !== null && <FichePosee bulleId={montree} />}
      </section>
    );
  }

  return (
    <section className="journee encours" aria-label={MA_JOURNEE} data-tuto="journee">
      <div className="encours-compte">
        <div className="encours-barre" aria-hidden="true">
          <i style={{ width: `${compte.total > 0 ? (100 * compte.faites) / compte.total : 0}%` }} />
        </div>
        <p className="encours-compte-mots">{faitesEtPrevues(compte.faites, compte.total, dureeLisible(compte.minutes))}</p>
      </div>

      {annuler && (
        <p className="encours-annuler" role="status">
          <span>{annuler.texte}</span>
          <button type="button" disabled={occupe} data-geste="annuler" onClick={() => basculer(annuler.tache, false)}>
            {ANNULER}
          </button>
        </p>
      )}
      {fini && <p className="encours-tout-fait">{TOUT_EST_FAIT}</p>}

      <ol className="journee-taches encours-lignes">
        {lignes.map((l) => {
          if (l.sorte === 'rendez_vous') {
            const r = l.rendezVous;
            return (
              <li key={`rdv-${r.id}`} className="encours-ligne encours-ligne--rdv" data-rdv={r.id}>
                <span className="encours-heure">{heureLisible(r.heure)}</span>
                <span className="encours-quoi">
                  <b>{r.titre}</b>
                  <small>{[IMPOSE, r.dossier?.titre, r.dureeMin ? dureeLisible(r.dureeMin) : null].filter(Boolean).join(' · ')}</small>
                </span>
              </li>
            );
          }
          const t = l.tache;
          const fait = t.etat === 'fait';
          const projet = [dureeLisible(t.dureeMin), t.dossier?.titre].filter(Boolean).join(' · ');
          // Sur la page du cahier, le projet puis la durée (maquette `journee()`, décision 5 : rien d'autre).
          const ligneGrise = [t.dossier?.titre, dureeLisible(t.dureeMin)].filter(Boolean).join(' · ');
          if (deLHeure?.id === t.bulleId) {
            return (
              <li key={t.bulleId} className="encours-une" data-tache={t.bulleId} data-de-l-heure="oui" style={teinte(t)}>
                <p className="encours-une-quand">
                  {MOTS_DE_L_HEURE[deLHeure.quand]} · {heureLisible(t.heure)}
                </p>
                <button type="button" className="encours-une-titre" onClick={() => onOuvrirLaFiche(t)}>
                  {t.titre}
                </button>
                <p className="encours-une-projet">{[projet, t.libere.length > 0 ? libereDesChoses(t.libere.length) : null].filter(Boolean).join(' · ')}</p>
                <div className="encours-une-gestes">
                  <button
                    type="button"
                    className="encours-fait"
                    data-tuto="journee-rond"
                    data-journee-rond={t.bulleId}
                    aria-label={cEstFaitPour(t.titre)}
                    disabled={occupe || annuler?.tache.bulleId === t.bulleId}
                    onClick={() => basculer(t, true)}
                  >
                    {C_EST_FAIT}
                  </button>
                  <button
                    type="button"
                    className="encours-plus-tard"
                    aria-label={plusTardPour(t.titre)}
                    data-geste="plus-tard"
                    disabled={occupe || annuler?.tache.bulleId === t.bulleId}
                    onClick={() => setPlusTard(t)}
                  >
                    {PLUS_TARD}
                  </button>
                </div>
              </li>
            );
          }
          return (
            <li key={t.bulleId} className={`encours-ligne journee-tache${fait ? ' est-faite' : ''}`} data-tache={t.bulleId} style={teinte(t)}>
              <span className="encours-heure">{heureLisible(t.heure)}</span>
              <button
                type="button"
                className={`journee-rond pouce-case${fait ? ' est-faite' : ''}`}
                data-tuto="journee-rond"
                data-journee-rond={t.bulleId}
                aria-pressed={fait}
                aria-label={fait ? remettreAFaire(t.titre) : cEstFaitPour(t.titre)}
                disabled={occupe}
                onClick={() => basculer(t, !fait)}
              />
              <button type="button" className="encours-titre" onClick={() => onOuvrirLaFiche(t)}>
                <span className="encours-quoi">
                  <b>{t.titre}</b>
                  <small>{ligneGrise}</small>
                </span>
                <span className="encours-chev" aria-hidden="true">
                  ›
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <button type="button" className="encours-changer" data-geste="changer" onClick={onChanger}>
        {CHANGER_MA_JOURNEE} ›
      </button>

      {plusTard && (
        <FeuilleDuBas titre={PLUS_TARD} onFermer={() => setPlusTard(null)}>
          <div className="choisis-feuille" data-feuille-de="plus-tard">
            <button
              type="button"
              className="choisis-feuille-ligne"
              data-geste="plus-tard-aujourdhui"
              onClick={() => plusTardAujourdhuiPour(plusTard)}
            >
              <span className="choisis-feuille-mots">
                {PLUS_TARD_AUJOURDHUI}
                <small>{PLUS_TARD_AUJOURDHUI_NOTE}</small>
              </span>
            </button>
            <button type="button" className="choisis-feuille-ligne" data-geste="pas-aujourdhui" onClick={() => pasAujourdhui(plusTard)}>
              <span className="choisis-feuille-mots">
                {PAS_AUJOURDHUI}
                <small>{PAS_AUJOURDHUI_NOTE}</small>
              </span>
            </button>
          </div>
        </FeuilleDuBas>
      )}
    </section>
  );
}
